import React from "react";
import { Box, Flex, HStack, VStack, Text, Icon, Badge, Button } from "@chakra-ui/react";
import { FaTicketAlt, FaUser, FaBuilding, FaCalendarAlt } from "react-icons/fa";
import TicketActions from "../../../../../components/TicketActions";
import { useTranslation } from 'react-i18next';

export default function TicketHeader({
  ticket,
  colorScheme,
  headerPadding,
  titleSize,
  iconSize,
  statusConfig,
  MotionBox,
  assignTicketModalState,
  assignUnitModalState,
  handleDelete,
  handleStart,
  handleComplete,
  handleCancel,
  handlePrint,
  handleCreateInvoice,
  handleScrollToTasks,
  serviceFormModalState,
  liveSupportModalState,
}) {
  const { t, i18n } = useTranslation();

  const formatDate = (dateStr) => {
    if (!dateStr || isNaN(new Date(dateStr))) return t('ticket.header.date_not_specified');
    const locale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
    return new Date(dateStr).toLocaleDateString(locale, { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  return (
    <MotionBox
      bg={colorScheme.cardBg}
      borderRadius="2xl"
      shadow="2xl"
      border="1px solid"
      borderColor={colorScheme.borderColor}
      mb={10}
      overflow="hidden"
      position="relative"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
      _hover={{ shadow: "3xl", transform: "translateY(-2px)" }}
      sx={{ transition: "all 0.3s ease" }}
    >
      {/* Decorative Background Pattern */}
      <Box
        position="absolute"
        top={0}
        right={0}
        width="200px"
        height="200px"
        bgGradient="radial(circle, whiteAlpha.100 0%, transparent 70%)"
        pointerEvents="none"
      />

      {/* Enhanced Header Bar */}
      <Box bgGradient={colorScheme.headerBg} px={headerPadding} py={headerPadding} position="relative">
        <Flex
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          direction={{ base: "column", md: "row" }}
          gap={{ base: 4, md: 0 }}
        >
          <HStack spacing={{ base: 3, md: 4, lg: 6 }}>
            <Box bg="whiteAlpha.200" p={{ base: 2, md: 3 }} borderRadius="xl" backdropFilter="blur(10px)">
              <Icon as={FaTicketAlt} color="white" boxSize={iconSize} />
            </Box>
            <VStack align="start" spacing={{ base: 1, md: 2 }}>
              <Badge colorScheme="blue" variant="subtle">
                {t(`kinds.${ticket.kind}`, { defaultValue: ticket.kind })}
              </Badge>
              <Text color="white" fontSize={titleSize} fontWeight="bold" letterSpacing="tight">
                Ticket #{ticket.number}
              </Text>
            </VStack>
          </HStack>
        </Flex>
      </Box>

      {/* Quick Info Bar + Actions */}
      <Box
        bg={colorScheme.quickInfoBg}
        px={{ base: 6, md: 8, lg: 10 }}
        py={6}
        borderTop="1px solid"
        borderColor={colorScheme.quickInfoBorderColor}
      >
        <Flex justify="space-between" direction={{ base: "column", lg: "row" }} gap={{ base: 4, lg: 0 }}>
          <HStack spacing={{ base: 4, md: 6 }} flexWrap="wrap">
            <HStack spacing={2}>
              <Icon as={FaUser} color={colorScheme.mutedTextColor} boxSize={4} />
              <Text fontSize="sm" color={colorScheme.mutedTextColor}>{t('ticket.header.customer_label')}:</Text>
              <Text fontSize="sm" fontWeight="semibold" color={colorScheme.textColor}>
                {ticket.full_name || t('ticket.header.unassigned')}
              </Text>
            </HStack>

            <HStack justify="space-between">
              <HStack spacing={2}>
                <Icon as={FaBuilding} color={colorScheme.mutedTextColor} boxSize={4} />
                <Text fontSize="sm" color={colorScheme.mutedTextColor}>{t('ticket.header.reseller_label')}:</Text>
              </HStack>
              <HStack spacing={2}>
                <Text fontSize="sm" fontWeight="semibold" color={colorScheme.textColor}>
                  {ticket.assigned_to?.name || t('ticket.header.unassigned')}
                </Text>
                <Button size="xs" colorScheme="blue" variant="outline" onClick={assignTicketModalState.onOpen}>
                  {ticket.assigned_to ? t('ticket.header.change') : t('ticket.header.assign')}
                </Button>
              </HStack>
            </HStack>

            <HStack justify="space-between">
              <HStack spacing={2}>
                <Icon as={FaBuilding} color={colorScheme.mutedTextColor} boxSize={4} />
                <Text fontSize="sm" color={colorScheme.mutedTextColor}>{t('ticket.header.unit_label')}:</Text>
              </HStack>
              <HStack spacing={2}>
                <Text fontSize="sm" fontWeight="semibold" color={colorScheme.textColor}>
                  {ticket.related_unit?.name || t('ticket.header.unassigned')}
                </Text>
                <Button
                  size="xs"
                  colorScheme="green"
                  variant="outline"
                  onClick={assignUnitModalState.onOpen}
                  data-tour-id="assign-unit"
                >
                  {ticket.related_unit ? t('ticket.header.change') : t('ticket.header.assign')}
                </Button>
              </HStack>
            </HStack>

            <HStack spacing={2}>
              <Icon as={FaCalendarAlt} color={colorScheme.mutedTextColor} boxSize={4} />
              <Text fontSize="sm" color={colorScheme.mutedTextColor}>{t('ticket.header.created_label')}:</Text>
              <Text fontSize="sm" fontWeight="semibold" color={colorScheme.textColor}>
                {formatDate(ticket.created_at)}
              </Text>
            </HStack>
          </HStack>

          <Box>
            <TicketActions
              handleDelete={handleDelete}
              handleStart={handleStart}
              handleComplete={handleComplete}
              handleCancel={handleCancel}
              handlePrint={handlePrint}
              handleCreateInvoice={handleCreateInvoice}
              handleScrollToTasks={handleScrollToTasks}
              ticket={ticket}
            >
              <Button size="sm" colorScheme="teal" variant="outline" onClick={liveSupportModalState?.onOpen} isDisabled={!ticket}>
                Canlı Destek
              </Button>
              <Button size="sm" colorScheme="blue" variant="outline" onClick={serviceFormModalState?.onOpen} isDisabled={!ticket || ticket.number === undefined || ticket.number === null}>
                Servis Formu
              </Button>
            </TicketActions>
          </Box>
        </Flex>
      </Box>
    </MotionBox>
  );
}
